import { useCallback, useEffect, useRef, useState } from "react"
import { clampQuickWidgetBounds } from "../util/quickWidgets"

export type Position = {
    left: number,
    right: number,
    top: number,
    bottom: number
}

export type Bounds = Position & {
    width: number,
    height: number
}

export type DraggableReturnType<C, H> = {
    position?: Position,
    isDragging: boolean,
    containerRef: React.Ref<C>,
    handleRef: React.Ref<H>
}

const useDraggable = <C extends HTMLElement, H extends HTMLElement>(
    boundaryElement?: HTMLElement,
    onDragStart?: () => void,
    onDragEnd?: (position: Position) => void
) => {
    const containerRef = useRef<C>(null);
    const handleRef = useRef<H>(null);
    const containerStartPosition = useRef<Bounds>();
    const boundary = useRef<Position>();
    const [state, setState] = useState<{ position?: Position, isDragging: boolean }>({ isDragging: false })
    const cursorStartPosition = useRef<Position>()
    const currentPosition = useRef<Position>();
    const rafIdRef = useRef<number>();
    const pendingMouseEventRef = useRef<MouseEvent>();

    const clearDragVisuals = useCallback(() => {
        const container = containerRef.current;
        if (container) {
            container.style.transform = '';
            container.style.willChange = '';
        }
        if (rafIdRef.current !== undefined) {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = undefined;
        }
        pendingMouseEventRef.current = undefined;
        document.body.style.userSelect = '';
    }, []);

    const computePosition = useCallback((e: MouseEvent): Position | null => {
        const cursorStart = cursorStartPosition.current
        const containerStart = containerStartPosition.current
        if (!cursorStart || !containerStart) return null;

        const deltaX = e.clientX - cursorStart.left;
        const deltaY = e.clientY - cursorStart.top;

        let newLeft = containerStart.left + deltaX;
        let newTop = containerStart.top + deltaY;

        const containerWidth = containerStart.width;
        const containerHeight = containerStart.height;

        const clamped = clampQuickWidgetBounds({
            left: newLeft,
            top: newTop,
            width: containerWidth,
            height: containerHeight,
        });

        const newRight = window.innerWidth - clamped.left - clamped.width;

        return {
            left: clamped.left,
            top: clamped.top,
            right: newRight,
            bottom: window.innerHeight - clamped.top - clamped.height
        };
    }, []);

    const applyDragTransform = useCallback((e: MouseEvent) => {
        const containerStart = containerStartPosition.current;
        const newPosition = computePosition(e);
        if (!containerStart || !newPosition) return;

        currentPosition.current = newPosition;

        const deltaX = newPosition.left - containerStart.left;
        const deltaY = newPosition.top - containerStart.top;

        const container = containerRef.current;
        if (container) {
            container.style.transform = `translate3d(${deltaX}px, ${deltaY}px, 0)`;
        }
    }, [computePosition]);

    const onMouseMoveRef = useRef<(e: MouseEvent) => void>(() => {});
    const onMouseUpRef = useRef<() => void>(() => {});

    const onMouseMoveHandler = useCallback((e: MouseEvent) => {
        onMouseMoveRef.current(e);
    }, []);

    const onMouseUpHandler = useCallback(() => {
        onMouseUpRef.current();
    }, []);

    onMouseMoveRef.current = (e: MouseEvent) => {
        pendingMouseEventRef.current = e;
        if (rafIdRef.current !== undefined) return;

        rafIdRef.current = requestAnimationFrame(() => {
            rafIdRef.current = undefined;
            const pendingEvent = pendingMouseEventRef.current;
            if (pendingEvent) {
                applyDragTransform(pendingEvent);
            }
        });
    };

    onMouseUpRef.current = () => {
        const finalPosition = currentPosition.current;
        if (finalPosition) {
            onDragEnd?.(finalPosition);
        }
        clearDragVisuals();

        setState({
            position: finalPosition,
            isDragging: false
        });

        document.removeEventListener("mouseup", onMouseUpHandler)
        document.removeEventListener("mousemove", onMouseMoveHandler)
        document.removeEventListener("contextmenu", onMouseUpHandler)
    };

    const onMouseDown = useCallback((e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;

        const container = containerRef.current
        if (!container) return

        e.preventDefault()

        onDragStart?.()

        const bounds = container.getBoundingClientRect()
        if (boundaryElement) {
            const boundaryElementBounds = boundaryElement.getBoundingClientRect();
            boundary.current = {
                left: boundaryElementBounds.left,
                top: boundaryElementBounds.top,
                right: window.innerWidth - boundaryElementBounds.right,
                bottom: window.innerHeight - boundaryElementBounds.bottom
            }
        }

        containerStartPosition.current = {
            left: bounds.left,
            top: bounds.top,
            right: window.innerWidth - bounds.right,
            bottom: window.innerHeight - bounds.bottom,
            width: bounds.width,
            height: bounds.height
        };
        cursorStartPosition.current = {
            left: e.clientX,
            right: window.innerWidth - e.clientX,
            top: e.clientY,
            bottom: window.innerHeight - e.clientY
        };

        currentPosition.current = {
            left: bounds.left,
            top: bounds.top,
            right: window.innerWidth - bounds.right,
            bottom: window.innerHeight - bounds.bottom
        };

        container.style.willChange = 'transform';
        document.body.style.userSelect = 'none';

        setState({
            position: currentPosition.current,
            isDragging: true
        })

        document.addEventListener("mousemove", onMouseMoveHandler)
        document.addEventListener("mouseup", onMouseUpHandler)
        document.addEventListener("contextmenu", onMouseUpHandler)
    }, [boundaryElement, clearDragVisuals, onDragStart, onMouseMoveHandler, onMouseUpHandler]);

    useEffect(() => {
        const handle = handleRef.current;
        if (handle) {
            handle.addEventListener("mousedown", onMouseDown)
        }
        return () => {
            if (handle) {
                handle.removeEventListener("mousedown", onMouseDown)
            }
            clearDragVisuals();
        }
    }, [onMouseDown, clearDragVisuals])

    return {
        ...state,
        containerRef,
        handleRef
    }
}

export default useDraggable
